import styles from './PFPBuilderGate.module.css';
import { useState, useEffect, useRef } from 'react';
import PFPBuilder from './PFPBuilder';
import { Box, Container, Stack } from '@chakra-ui/react';
import layers from '../public/pfp_builder_resources/config/layers.json';
import steps from '../public/pfp_builder_resources/config/steps.json';

import { useWallet } from '../web3/WalletContext';

function PFPBuilderGate() {
  const { provider, chainId, address } = useWallet();

  // useEffect(() => {
  //   if (provider) {}
  // }, [provider]);

  /* Constants */
  /* ---------------------------------------------------------------------- */

  const dates = [
    new Date().toISOString(), // DEBUG: Always open now.
    '2022-03-25 4:00 PM EDT',
    '2022-03-25 5:00 PM EDT',
    '2022-06-13 1:00 PM EDT',
    '2022-07-13 1:00 PM EDT',
    '2022-07-14 1:00 PM EDT',
    '2022-07-15 1:00 PM EDT',
    '2022-07-16 1:00 PM EDT',
    '2022-07-17 1:00 PM EDT',
  ];

  const inductionLength = 60;

  const code = 'test phrase. click validate to continue.';

  /* Ref */
  /* ---------------------------------------------------------------------- */

  const firstUpdate = useRef(true);

  const validationTextInput = useRef(null);

  /* State */
  /* ---------------------------------------------------------------------- */

  const [hasPassedValidation, setHasPassedValidation] = useState(false);

  const [hasFailedValidation, setHasFailedValidation] = useState(false);

  /* Init */
  /* ---------------------------------------------------------------------- */

  function init() {
    if (isInductionOpen() === false) {
      sessionStorage.removeItem('pfp-builder-gate-has-passed-validation');
    }

    // http://localhost:3000/mint-pfp/?skip=1
    const queryParams = new URLSearchParams(window.location.search);
    const skipInduction = queryParams.get('skip');
    if (skipInduction) {
      setHasPassedValidation(true);

      return;
    }

    // sessionStorage.removeItem('pfp-builder-gate-has-passed-validation'); // DEBUG

    setHasPassedValidation(
      Boolean(sessionStorage.getItem('pfp-builder-gate-has-passed-validation')),
    );
  }

  /* Conditions */
  /* ---------------------------------------------------------------------- */

  function isInductionOpen(): boolean {
    return getActiveDates().length !== 0;
  }

  function isValidationTextCorrect(): boolean {
    return code.toLowerCase() === validationTextInput.current.value.toLowerCase();
  }

  /* Get Data */
  /* ---------------------------------------------------------------------- */

  function addMinutes(date: Date, minutes: number): Date {
    return new Date(date.getTime() + minutes * 60000);
  }

  function getActiveDates(): string[] {
    return dates.filter((dateStr: string) => {
      const now = new Date();
      const from = new Date(dateStr);
      const to = addMinutes(from, inductionLength);

      return from <= now && to >= now;
    });
  }

  function getFutureDates(): string[] {
    return dates.filter((dateStr: string) => {
      const now = new Date();
      const from = new Date(dateStr);
      const to = addMinutes(from, inductionLength);

      return to >= now;
    });
  }

  function getNextInductionDate(): string {
    return dates[dates.length - 1];
  }

  function getFormattedDate(dateStr: string): string {
    const date = new Date(dateStr);

    const day = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });

    return day + ' ' + time;
  }

  /* Actions */
  /* ---------------------------------------------------------------------- */

  function handleValidateClick() {
    const isValid = isValidationTextCorrect();
    setHasPassedValidation(isValid);
    setHasFailedValidation(!isValid);
    sessionStorage.setItem('pfp-builder-gate-has-passed-validation', isValid ? '1' : '0');
  }

  /* Bootstrap */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (firstUpdate.current === false) {
      return;
    }

    if (firstUpdate.current) {
      firstUpdate.current = false;
    }

    // Init
    init();
  });

  /* Components */
  /* ---------------------------------------------------------------------- */

  function InductionIsOpen() {
    return (
      <section className={styles['pfp-builder-gate__current-induction']}>
        <h2 className={styles['pfp-builder-gate__current-induction-title']}>Induction Open</h2>
        <p className={styles['pfp-builder-gate__current-induction-instructions']}>
          Join us in the{' '}
          <a
            className={styles['pfp-builder-gate__current-induction-link']}
            target="_blank"
            rel="noreferrer"
            href="https://example.com"
          >
            Song-A-Day Discord
          </a>{' '}
          to participate.
        </p>

        {!provider && <p>Please connect your wallet.</p>}
        {provider && (
          <div className={styles['pfp-builder-gate__current-induction-form']}>
            <input
              className={styles['pfp-builder-gate__current-induction-input']}
              placeholder="Enter the pass phrase here."
              type="text"
              name="validation-text"
              ref={validationTextInput}
              defaultValue={code}
            />
            <button
              className={styles['pfp-builder-gate__current-induction-button']}
              onClick={() => handleValidateClick()}
            >
              Validate!
            </button>
          </div>
        )}

        {hasFailedValidation && (
          <div className={styles['pfp-builder-gate__current-induction-error']}>
            Sorry, but the phrase entered is incorrect.
          </div>
        )}
      </section>
    );
  }

  function InductionIsClosed() {
    return (
      <section className={styles['pfp-builder-gate__next-induction']}>
        <h2 className={styles['pfp-builder-gate__next-induction-title']}>Next Induction</h2>
        <h3 className={styles['pfp-builder-gate__next-induction-date']}>
          {getFormattedDate(getNextInductionDate())}
        </h3>
        <p className={styles['pfp-builder-gate__next-induction-description']}>
          On {getFormattedDate(getNextInductionDate())} join us in the{' '}
          <a
            className={styles['pfp-builder-gate__next-induction-link']}
            target="_blank"
            rel="noreferrer"
            href="https://example.com"
          >
            Song-A-Day Discord
          </a>{' '}
          to participate.
        </p>
      </section>
    );
  }

  function UpcomingDates() {
    return (
      <section className={styles['pfp-builder-gate__upcoming-inductions']}>
        <h4 className={styles['pfp-builder-gate__upcoming-inductions-title']}>
          Upcoming Induction Dates
        </h4>
        <ul className={styles['pfp-builder-gate__upcoming-inductions-dates']}>
          {getFutureDates().map((date) => (
            <li key={date} className={styles['pfp-builder-gate__upcoming-inductions-date']}>
              {getFormattedDate(date)}
            </li>
          ))}
        </ul>
      </section>
    );
  }

  function GateHeader() {
    return (
      <header className={styles['pfp-builder-gate__header']}>
        <h1 className={styles['pfp-builder-gate__title']}>Induction Ceremony</h1>
      </header>
    );
  }

  function GateMain() {
    return (
      <main className={styles['pfp-builder-gate__main']}>
        {isInductionOpen() && <InductionIsOpen></InductionIsOpen>}
        {!isInductionOpen() && <InductionIsClosed></InductionIsClosed>}
      </main>
    );
  }

  /* Main Component */
  /* ---------------------------------------------------------------------- */

  return (
    <div>
      {!hasPassedValidation && (
        <div className={styles['pfp-builder-gate']}>
          <div className={styles['pfp-builder-gate__bg']}>
            <Box>
              <Container py={16} maxWidth="container.xl">
                <Stack spacing={4}>
                  <div>
                    <GateHeader></GateHeader>
                    <GateMain></GateMain>
                  </div>
                </Stack>
              </Container>
            </Box>
          </div>
          <Box bgGradient="linear(to-b, brand.lightTeal, transparent)">
            <div className={styles['pfp-builder-gate__aside']}>
              <Container py={16} maxWidth="container.lg">
                <Stack spacing={4}>
                  <div>
                    <UpcomingDates></UpcomingDates>
                  </div>
                </Stack>
              </Container>
            </div>
          </Box>
        </div>
      )}
      {hasPassedValidation && (
        <Box bgGradient="linear(to-b, brand.lightTeal, transparent)">
          <Container py={16} maxWidth="container.xl">
            {!provider && (
              <Stack spacing={4} justify="center" align="center">
                <div>
                  <p>Please connect your wallet.</p>
                </div>
              </Stack>
            )}

            <Stack spacing={4}>
              <div>
                {provider && (
                  <PFPBuilder
                    signerUri="https://idchain.songadao.org/songadao-pfp-builder-api/mint/" // Prod
                    // signerUri="http://localhost:5000/songadao-pfp-builder-api/mint/" // Local
                    //
                    // contractAddr="TODO" // Mainnet
                    contractAddr="0x3502b05cCCB22644937D10eBD74546F9c2bC6564" // Rinkeby
                    // contractAddr="0x5FbDB2315678afecb367f032d93F642f64180aa3" // Hardhat
                    //
                    ipfsUrl="https://gateway.pinata.cloud/ipfs/{hash}"
                    // ipfsUrl="https://{hash}.ipfs.nftstorage.link/"
                    layers={layers}
                    steps={steps}
                    provider={provider}
                    chainId={chainId}
                    address={address}
                  ></PFPBuilder>
                )}
              </div>
            </Stack>
          </Container>
        </Box>
      )}
    </div>
  );
}

export default PFPBuilderGate;
